/**
 * SettingsPage — role-based hub
 * /settings
 *
 * DRIVER: view own CDL + contact info, see assigned equipment
 * CARRIER_ADMIN / ADMIN / DISPATCHER: link to driver roster management
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useState } from 'react'
import { settingsApi } from '../lib/api'
import { useAuth } from '../lib/auth-context'

function ComplianceBadge({ date, label }: { date?: string | null; label: string }) {
  if (!date) return <span className="text-xs text-gray-400">— Not set</span>
  const exp = new Date(date)
  const now = new Date()
  const warn90 = new Date(now); warn90.setDate(now.getDate() + 90)
  const expired = exp <= now
  const expiring = !expired && exp <= warn90
  const cls = expired
    ? 'bg-red-100 text-red-800 border border-red-200'
    : expiring
      ? 'bg-amber-100 text-amber-800 border border-amber-200'
      : 'bg-green-100 text-green-700 border border-green-200'
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {expired ? '⚠ EXPIRED' : expiring ? '⚠ Expiring' : '✓'} {label}: {date}
    </span>
  )
}

export default function SettingsPage() {
  const { user, hasRole } = useAuth()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [phone, setPhone] = useState('')
  const [ecName, setEcName] = useState('')
  const [ecPhone, setEcPhone] = useState('')
  const [saveMsg, setSaveMsg] = useState<string | null>(null)

  const isDriver    = hasRole('DRIVER')
  const isAdminLike = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN', 'DISPATCHER')

  const profileQ = useQuery({
    queryKey: ['settings-profile'],
    queryFn: () => settingsApi.getProfile().then(r => r.data.profile),
  })
  const equipQ = useQuery({
    queryKey: ['settings-equipment'],
    queryFn: () => settingsApi.getEquipment().then(r => r.data.assignment),
    enabled: isDriver || isAdminLike,
  })

  const saveMut = useMutation({
    mutationFn: () => settingsApi.updateProfile({ phone, emergencyContactName: ecName, emergencyContactPhone: ecPhone }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings-profile'] })
      setEditing(false)
      setSaveMsg('Profile updated.')
      setTimeout(() => setSaveMsg(null), 3000)
    },
  })

  const profile = profileQ.data

  function startEdit() {
    setPhone(profile?.phone ?? '')
    setEcName(profile?.emergencyContactName ?? '')
    setEcPhone(profile?.emergencyContactPhone ?? '')
    setEditing(true)
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">{user?.name ?? user?.email} · {user?.accountType}</p>
      </div>

      {saveMsg && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-3 text-sm">{saveMsg}</div>
      )}

      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Settings Navigation</h2>
        <p className="text-sm text-gray-500 mb-4">Jump directly to settings and admin tools based on your role.</p>
        <div className="flex flex-wrap gap-2">
          {isDriver && (
            <Link to="/driver/settings" className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              My Settings
            </Link>
          )}
          {isAdminLike && (
            <>
              <Link to="/settings/drivers" className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Driver Roster Settings
              </Link>
              <Link to="/carrier/equipment" className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Fleet Equipment
              </Link>
              <Link to="/carrier/fleet/finance" className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Fleet Finance
              </Link>
            </>
          )}
        </div>
      </section>

      {/* ── Driver Profile ────────────────────────────────────────────── */}
      {(isDriver || isAdminLike) && (
        <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">My Profile</h2>
            {!editing && (
              <button onClick={startEdit}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                Edit contact info
              </button>
            )}
          </div>

          {profileQ.isLoading && <p className="text-sm text-gray-400">Loading…</p>}
          {profile && (
            <div className="space-y-4">
              {/* CDL info — read-only for drivers, carrier admin manages this */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">CDL Class</div>
                  <div className="font-medium text-gray-900">{profile.cdlClass ?? <span className="text-gray-400">Not set</span>}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">License State</div>
                  <div className="font-medium text-gray-900">{profile.licenseState ?? <span className="text-gray-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">License Number</div>
                  <div className="font-medium text-gray-900">{profile.licenseNumber ?? <span className="text-gray-400">—</span>}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Experience</div>
                  <div className="font-medium text-gray-900">
                    {profile.yearsExperience != null ? `${profile.yearsExperience} yr` : <span className="text-gray-400">—</span>}
                  </div>
                </div>
              </div>

              {/* Compliance badges */}
              <div className="flex flex-wrap gap-2">
                <ComplianceBadge date={profile.licenseExpires} label="CDL Exp" />
                <ComplianceBadge date={profile.medicalCertExpires} label="Med Cert" />
              </div>

              {/* Endorsements */}
              {profile.endorsements?.length > 0 && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Endorsements</div>
                  <div className="flex gap-1 flex-wrap">
                    {profile.endorsements.map(e => (
                      <span key={e} className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">{e}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Editable contact fields */}
              {editing ? (
                <div className="border-t pt-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                    <input value={phone} onChange={e => setPhone(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Emergency Contact Name</label>
                    <input value={ecName} onChange={e => setEcName(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Emergency Contact Phone</label>
                    <input value={ecPhone} onChange={e => setEcPhone(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={() => saveMut.mutate()}
                      disabled={saveMut.isPending}
                      className="bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-blue-800 disabled:opacity-50">
                      {saveMut.isPending ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setEditing(false)}
                      className="text-sm text-gray-600 hover:text-gray-900 px-4 py-2 rounded-lg border">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Phone</div>
                    <div className="text-gray-900">{profile.phone ?? <span className="text-gray-400">—</span>}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Emergency Contact</div>
                    <div className="text-gray-900">
                      {profile.emergencyContactName
                        ? `${profile.emergencyContactName}${profile.emergencyContactPhone ? ` · ${profile.emergencyContactPhone}` : ''}`
                        : <span className="text-gray-400">—</span>}
                    </div>
                  </div>
                  {profile.unitLabel && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Unit Label</div>
                      <div className="font-medium text-gray-900">{profile.unitLabel}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ── My Equipment ──────────────────────────────────────────────── */}
      {(isDriver || isAdminLike) && (
        <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">My Equipment</h2>
          {equipQ.isLoading && <p className="text-sm text-gray-400">Loading…</p>}
          {equipQ.data ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Tractor</div>
                  <div className="font-medium text-gray-900">
                    {equipQ.data.tractorManufacturer && equipQ.data.tractorModel
                      ? `${equipQ.data.tractorManufacturer} ${equipQ.data.tractorModel}`
                      : <span className="text-gray-400">—</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Trailer</div>
                  <div className="font-medium text-gray-900">
                    {equipQ.data.trailerManufacturer && equipQ.data.trailerModel
                      ? `${equipQ.data.trailerManufacturer} ${equipQ.data.trailerModel}`
                      : <span className="text-gray-400">—</span>}
                  </div>
                </div>
                {equipQ.data.unitLabel && (
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Unit Label</div>
                    <div className="font-medium text-blue-800">{equipQ.data.unitLabel}</div>
                  </div>
                )}
                {equipQ.data.comboKey && (
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Combo Key</div>
                    <div className="font-mono text-xs text-gray-600">{equipQ.data.comboKey}</div>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-400 pt-2">
                Equipment assigned by your carrier. Contact your dispatcher to change assignments.
              </p>
            </div>
          ) : (
            !equipQ.isLoading && (
              <div className="text-sm text-gray-400 italic">
                No equipment assigned yet. Ask your carrier admin to assign an equipment combo.
              </div>
            )
          )}
        </section>
      )}

      {/* ── Carrier Admin: Driver Roster ──────────────────────────────── */}
      {isAdminLike && (
        <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Driver Roster</h2>
          <p className="text-sm text-gray-500 mb-4">
            Manage driver profiles, CDL compliance, and equipment assignments for your fleet.
          </p>
          <Link
            to={`/settings/drivers?carrierId=${user?.orgId ?? ''}`}
            className="inline-flex items-center gap-2 bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-800"
          >
            View Driver Roster →
          </Link>
        </section>
      )}
    </div>
  )
}
